// @flow
import React, { type Node, useContext } from 'react';
import { get, isEmpty, isFunction } from 'lodash';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import { type Props as DynamicLinkProps } from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { type TagProps } from 'Components/frameworks/tag/Tag.jsx';
import { type TextProps } from 'Components/frameworks/text/Text.jsx';
import { type TextSeeMoreProps } from 'Components/frameworks/text-see-more/TextSeeMore.jsx';

import { CardBody } from './card-body/CardBody.jsx';
import { CardHeader } from './card-header/CardHeader.jsx';
import { CardMedia, type Media } from './card-media/CardMedia.jsx';
import { type CardHeadingProps } from './card-header/card-heading/CardHeading.jsx';
import * as layouts from './Card.layouts.jsx';
import * as CARD_LAYOUTS from './Card.constants';

export type CardProps = {
  className?: string,
  closeLabel?: string,
  color?: Colors,
  ctaLabel?: string,
  customMedia?: Node,
  heading?: CardHeadingProps,
  id?: string,
  introduction?: TextProps,
  layout?: $Keys<typeof CARD_LAYOUTS>,
  link?: DynamicLinkProps,
  media?: Media,
  seeLessLabel?: string,
  seeMoreLabel?: string,
  tags?: Array<TagProps>,
  text?: TextSeeMoreProps,
  theme?: string,
};

export function Card(props: CardProps) {
  const {
    className,
    closeLabel,
    color,
    ctaLabel,
    customMedia,
    heading,
    id,
    introduction,
    layout = CARD_LAYOUTS.INLINE_1,
    link,
    media,
    seeLessLabel,
    seeMoreLabel,
    tags,
    text,
    theme = 'default',
  } = props;

  const { isTablet } = useContext(ResponsiveContext);
  const hasTitle = !isEmpty(get(heading, 'title', ''));
  const hasCta = !isEmpty(ctaLabel);

  const header =
    isEmpty(get(introduction, 'text', '')) && !hasTitle ? null : (
      <CardHeader
        color={color}
        heading={heading}
        introduction={introduction}
        link={hasCta ? null : link}
        tags={tags}
        theme={theme}
      />
    );

  const body =
    isEmpty(get(text, 'text', '')) && !hasCta ? null : (
      <CardBody
        ctaLabel={ctaLabel}
        color={color}
        isTablet={isTablet}
        layout={layout}
        link={link}
        text={text}
        theme={theme}
        seeLessLabel={seeLessLabel}
        seeMoreLabel={seeMoreLabel}
      />
    );

  const picture =
    isEmpty(media && media.url) && !customMedia ? null : (
      <CardMedia
        closeLabel={closeLabel}
        link={hasCta || hasTitle ? null : link}
        media={media}
        theme={theme}
      >
        {customMedia}
      </CardMedia>
    );

  return body || header || picture ? (
    <article className={className} id={id}>
      {isFunction(layouts[layout])
        ? layouts[layout](header, body, picture)
        : layouts[CARD_LAYOUTS.INLINE_1](header, body, picture)}
    </article>
  ) : null;
}
